import type { FormikHelpers, FormikValues } from "formik";
import {
  createContext,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import type { ButtonProps } from "@citrine/atoms/button";
import { Button } from "@citrine/atoms/button";
import type { IconButtonProps } from "@citrine/atoms/icon-button";
import { IconButton } from "@citrine/atoms/icon-button";
import type { DataFullstoryId } from "@citrine/types/full-story";

import { useUniqueId } from "#src/hooks/use-unique-id";

import type { DialogProps } from "./dialog";
import { Dialog } from "./dialog";

type DialogButtonPropsBase<Values extends FormikValues = FormikValues> =
  DialogProps<Values> & {
    // TODO: allow for any button props
    disabled?: boolean;
    "data-testid"?: string;
    "data-fullstoryid"?: DataFullstoryId;
    getInitialValues: () => Values | undefined;
    ref?: React.ForwardedRef<DialogButtonRef>;
  };

export type LabeledDialogButtonProps<
  Values extends FormikValues = FormikValues,
> = DialogButtonPropsBase<Values> &
  ({
    icon?: undefined;
    label: React.ReactNode;
  } & Pick<
    ButtonProps,
    | "color"
    | "size"
    | "tooltip"
    | "variant"
    | "startIcon"
    | "endIcon"
    | "fullWidth"
    | "onMouseEnter"
    | "onMouseLeave"
  >);

type IconDialogButtonProps<Values extends FormikValues = FormikValues> =
  DialogButtonPropsBase<Values> &
    ({
      icon: React.ReactNode;
      label?: undefined;
      variant?: undefined;
      startIcon?: undefined;
      endIcon?: undefined;
      fullWidth?: undefined;
    } & Pick<
      IconButtonProps,
      "color" | "size" | "tooltip" | "onMouseEnter" | "onMouseLeave"
    >);

export type DialogButtonProps<Values extends FormikValues> =
  | LabeledDialogButtonProps<Values>
  | IconDialogButtonProps<Values>;

export interface DialogButtonRef {
  close: () => void;
}

const onCloseContext = createContext<{
  id?: string;
  onClose?: () => void;
}>({});
export const useCloseContext = () => useContext(onCloseContext);

export function DialogButton<Values extends FormikValues = FormikValues>({
  icon,
  label,
  disabled,
  "data-testid": dataTestId,
  "data-fullstoryid": fullStoryId,
  color,
  size,
  variant,
  startIcon,
  endIcon,
  getInitialValues,
  fullWidth,
  tooltip,
  onClose,
  onSubmit,
  onMouseEnter,
  onMouseLeave,
  className,
  ref,

  ...props
}: DialogButtonProps<Values>) {
  const [initialValues, setInitialValues] = useState<Values>();
  const onCloseCallback = useCallback(() => {
    setInitialValues(undefined);
    onClose?.();
  }, [setInitialValues, onClose]);

  const onSubmitCallback = useCallback(
    async (values: Values, formikHelpers: FormikHelpers<Values>) => {
      // allow onSubmit to block close with explicit return value of `false`
      if ((await onSubmit(values, formikHelpers)) !== false) {
        onCloseCallback();
      }
    },
    [onSubmit, onCloseCallback],
  );

  useImperativeHandle(ref, () => ({ close: onCloseCallback }), [
    onCloseCallback,
  ]);

  const onClick = useCallback(() => {
    setInitialValues(getInitialValues());
  }, [getInitialValues, setInitialValues]);

  const id = useUniqueId(props.id);
  const contextValue = useMemo(
    () => ({ id, onClose: onCloseCallback }),
    [id, onCloseCallback],
  );

  return (
    <>
      {icon ? (
        <IconButton
          className={className}
          data-testid={dataTestId}
          data-fullstoryid={fullStoryId}
          disabled={disabled}
          color={color}
          size={size as IconButtonProps["size"]}
          onClick={onClick}
          tooltip={tooltip}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {icon}
        </IconButton>
      ) : (
        <Button
          data-testid={dataTestId}
          data-fullstoryid={fullStoryId}
          className={className}
          disabled={disabled}
          color={color as ButtonProps["color"]}
          size={size}
          tooltip={tooltip}
          variant={variant}
          startIcon={startIcon}
          endIcon={endIcon}
          fullWidth={fullWidth}
          onClick={onClick}
        >
          {label}
        </Button>
      )}
      <onCloseContext.Provider value={contextValue}>
        <Dialog
          {...props}
          id={id}
          initialValues={initialValues}
          onClose={onCloseCallback}
          onSubmit={onSubmitCallback}
        />
      </onCloseContext.Provider>
    </>
  );
}
